import React, { useEffect, useState, useCallback } from "react";
import { openDocument } from "../utils/documents";
import { useParams, Link } from "react-router-dom";
import { platformAPI } from "../services/api";
import { MODULES } from "../constants/modules";
import { RINGS, RING_STYLE } from "../constants/rings";
import SendConfirmationButton from "../components/SendConfirmationButton";
import ShopDataTab from "../components/ShopDataTab";
import ShopDevicesTab from "../components/ShopDevicesTab";
import ShopDeletionTab from "../components/ShopDeletionTab";
import PackCard from "../components/PackCard";

const TABS = ["Overview", "Subscription", "Modules", "Devices", "Data", "Delete", "Audit log"];


function OverviewTab({ shopId }) {
  const [scorecard, setScorecard] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    platformAPI
      .shopScorecard(shopId)
      .then(({ data }) => setScorecard(data))
      .catch((err) => setError(err.response?.data?.error || "Failed to load"));
  }, [shopId]);

  if (error) return <p className="text-red-600">{error}</p>;
  if (!scorecard) return <p className="text-gray-500">Loading…</p>;

  const stats = [
    { label: `Revenue (${scorecard.period_days}d)`, value: scorecard.revenue },
    { label: "Sales", value: scorecard.sales_count },
    { label: "Active devices", value: scorecard.active_devices },
    { label: "Active staff", value: scorecard.active_staff },
  ];

  return (
    <div>
      {scorecard.at_risk && (
        <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          ⚠️ No recent sales — this shop may not be actively using the solution.
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl shadow p-4">
            <p className="text-xs text-gray-500">{stat.label}</p>
            <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
          </div>
        ))}
      </div>
      <p className="text-sm text-gray-500 mt-4">
        Last sale: {scorecard.last_sale_at ? new Date(scorecard.last_sale_at).toLocaleString() : "never"}
      </p>

      {scorecard.activation && (
        <div className="bg-white rounded-xl shadow p-4 mt-4 max-w-md">
          <h3 className="font-semibold text-gray-800 mb-2">Activation</h3>
          <dl className="text-sm space-y-1">
            {[
              ["Signed up", scorecard.activation.signup_at],
              ["Email confirmed", scorecard.activation.email_verified_at],
              ["First product", scorecard.activation.first_product_at],
              ["First device", scorecard.activation.first_device_at],
              ["First sale", scorecard.activation.first_sale_at],
              ["First staff member", scorecard.activation.first_staff_at],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between gap-3">
                <dt className="text-gray-500">{label}</dt>
                <dd className={value ? "text-gray-900" : "text-amber-600"}>
                  {value ? new Date(value).toLocaleString() : "not yet"}
                </dd>
              </div>
            ))}
          </dl>
          {scorecard.activation.owner_email && (
            <p className="text-sm text-gray-600 mt-3">
              Owner: <code>{scorecard.activation.owner_email}</code>
            </p>
          )}
          {!scorecard.activation.email_verified_at && (
            <SendConfirmationButton shopId={shopId} className="mt-2" />
          )}
        </div>
      )}
    </div>
  );
}

const METHOD_LABELS = {
  cash: "Cash",
  d17: "D17",
  flouci: "Flouci",
  bank_transfer: "Bank transfer",
  card: "Card",
  other: "Other",
};

const methodLabel = (key) => METHOD_LABELS[key] || key;

/** Money received: the only action that should move a paid shop's dates. */
function PaymentsCard({ shop, settings, onChange }) {
  const [payments, setPayments] = useState([]);
  const [form, setForm] = useState({ amount: shop.subscription_price ?? "", method: "", reference: "", documentType: "", periodEnd: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const load = useCallback(() => {
    platformAPI.listPayments(shop.id).then(({ data }) => setPayments(data));
  }, [shop.id]);

  useEffect(load, [load]);

  const methods = settings?.payment_methods || [];

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setDone(null);

    try {
      const { data } = await platformAPI.recordPayment(shop.id, {
        amount: Number(form.amount),
        method: form.method || methods[0],
        reference: form.reference || undefined,
        documentType: form.documentType || undefined,
        periodEnd: form.periodEnd || undefined,
        notes: form.notes || undefined,
      });
      setDone(data);
      setForm({ ...form, reference: "", periodEnd: "", notes: "" });
      load();
      onChange();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to record the payment");
    } finally {
      setBusy(false);
    }
  };

  const viewDocument = async (paymentId) => {
    try {
      await openDocument(async () => (await platformAPI.paymentDocument(shop.id, paymentId)).data);
    } catch (err) {
      setError(
        err.message === "popup-blocked"
          ? "Your browser blocked the new tab. Allow pop-ups for this site and try again."
          : "Could not open the document"
      );
    }
  };

  return (
    <div className="bg-white rounded-xl shadow p-5 space-y-3">
      <h3 className="font-semibold text-gray-800">Record a payment</h3>
      <p className="text-xs text-gray-500">
        Records the money, issues a numbered receipt, makes the shop active and moves its paid period forward
        (a suspended shop is reactivated). Do this for every payment, whatever the method.
      </p>

      {error && <p className="text-red-600 text-sm">{error}</p>}
      {done && (
        <p className="text-green-700 text-sm">
          Recorded as {done.document_number}. Paid until {done.period_end}
          {done.reactivated ? " — the shop was reactivated" : ""}.
        </p>
      )}

      <form onSubmit={submit} className="grid grid-cols-2 gap-3">
        <label className="text-sm text-gray-600">
          Amount ({shop.currency})
          <input
            type="number" step="0.01" min="0.01" required
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
          />
        </label>
        <label className="text-sm text-gray-600">
          Method
          <select
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={form.method || methods[0] || ""}
            onChange={(e) => setForm({ ...form, method: e.target.value })}
          >
            {methods.map((m) => <option key={m} value={m}>{methodLabel(m)}</option>)}
          </select>
        </label>
        <label className="text-sm text-gray-600">
          Reference
          <input
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Transfer or D17 reference"
            value={form.reference}
            onChange={(e) => setForm({ ...form, reference: e.target.value })}
          />
        </label>
        <label className="text-sm text-gray-600">
          Document
          <select
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={form.documentType || settings?.default_document_type || "receipt"}
            onChange={(e) => setForm({ ...form, documentType: e.target.value })}
          >
            <option value="receipt">Receipt</option>
            <option value="invoice">Invoice</option>
          </select>
        </label>
        <label className="text-sm text-gray-600 col-span-2">
          Paid until (optional)
          <input
            type="date"
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={form.periodEnd}
            onChange={(e) => setForm({ ...form, periodEnd: e.target.value })}
          />
          <span className="block text-xs text-gray-500 mt-1">
            Empty: one {shop.billing_cycle === "yearly" ? "year" : "month"} from the end of the current period
            (or from today if it has ended).
          </span>
        </label>
        <label className="text-sm text-gray-600 col-span-2">
          Notes
          <input
            className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </label>
        <div className="col-span-2">
          <button
            type="submit" disabled={busy}
            className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
          >
            Record payment
          </button>
        </div>
      </form>

      <h4 className="font-medium text-gray-700 pt-2">Payments</h4>
      {payments.length === 0 ? (
        <p className="text-sm text-gray-400">None yet.</p>
      ) : (
        <ul className="divide-y text-sm">
          {payments.map((p) => (
            <li key={p.id} className="py-2 flex items-center justify-between gap-3">
              <span>
                <span className="font-medium">{p.document_number}</span>{" "}
                <span className="text-gray-500">
                  {Number(p.amount).toFixed(2)} {p.currency} · {methodLabel(p.method)} · {p.period_start} → {p.period_end}
                </span>
              </span>
              <button onClick={() => viewDocument(p.id)} className="text-primary-700 hover:underline shrink-0">
                {p.document_type === "invoice" ? "Invoice" : "Receipt"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SubscriptionTab({ shop, onChange }) {
  const [settings, setSettings] = useState(null);
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState("");
  const [status, setStatus] = useState(shop.subscription_status);
  const [trialEndsAt, setTrialEndsAt] = useState(shop.trial_ends_at?.slice(0, 10) || "");
  const [periodEnd, setPeriodEnd] = useState(shop.current_period_end?.slice(0, 10) || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    platformAPI.listPlans().then(({ data }) => setPlans(data));
    platformAPI.getSettings().then(({ data }) => setSettings(data));
  }, []);

  const assign = async () => {
    if (!planId) return;
    setBusy(true);
    setError("");
    try {
      await platformAPI.assignPlan(shop.id, {
        plan_id: planId,
        subscription_status: status,
        trial_ends_at: trialEndsAt || null,
        current_period_end: periodEnd || null,
      });
      onChange();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to assign plan");
    } finally {
      setBusy(false);
    }
  };

  const saveOverride = async () => {
    setBusy(true);
    setError("");
    try {
      await platformAPI.overrideSubscription(shop.id, {
        subscription_status: status,
        trial_ends_at: trialEndsAt || null,
        current_period_end: periodEnd || null,
      });
      onChange();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update subscription");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-lg space-y-6">
      <div className="bg-white rounded-xl shadow p-5">
        <h3 className="font-semibold text-gray-800 mb-3">Current terms</h3>
        <dl className="text-sm text-gray-600 space-y-1">
          <div>Plan: {shop.plan_name || "— none assigned —"}</div>
          <div>Price: {shop.subscription_price ?? "—"} / {shop.billing_cycle}</div>
          <div>Device limit: {shop.device_limit}</div>
          {shop.subscription_status === "past_due" && shop.past_due_since && (
            <div className="text-red-600 font-medium">
              Past due since {new Date(shop.past_due_since).toLocaleString()} — locks automatically at{" "}
              {new Date(new Date(shop.past_due_since).getTime() + (settings?.grace_days ?? 3) * 24 * 3600 * 1000).toLocaleString()}.
            </div>
          )}
        </dl>
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <PaymentsCard shop={shop} settings={settings} onChange={onChange} />

      <div className="bg-white rounded-xl shadow p-5 space-y-3">
        <h3 className="font-semibold text-gray-800">Assign a plan</h3>
        <select
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          value={planId}
          onChange={(e) => setPlanId(e.target.value)}
        >
          <option value="">— choose a plan —</option>
          {plans.filter((p) => p.is_active).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} — {p.price}/{p.billing_cycle}
            </option>
          ))}
        </select>

        <label className="block text-sm text-gray-600">Status</label>
        <select
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {["trialing", "active", "past_due", "cancelled"].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <p className="text-xs text-gray-500">
          <strong>past_due</strong> locks the shop automatically after the grace period ({settings?.grace_days ?? 3} days, set in Settings). It is set for you when a
          trial or paid period ends — choose it by hand only to start that countdown yourself.
        </p>

        <label className="block text-sm text-gray-600">Trial ends</label>
        <input
          type="date"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          value={trialEndsAt}
          onChange={(e) => setTrialEndsAt(e.target.value)}
        />
        <p className="text-xs text-gray-500">
          The last day of the free trial. The owner is warned 7 days, 3 days and on the last day; the
          shop becomes past_due the day after.
        </p>

        <label className="block text-sm text-gray-600">Current period ends</label>
        <input
          type="date"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          value={periodEnd}
          onChange={(e) => setPeriodEnd(e.target.value)}
        />
        <p className="text-xs text-gray-500">
          The last day the shop has paid for — same warnings, and the same past_due countdown after it.
          Recording a payment moves it for you. Leave a date empty for no reminders or automatic locking.
        </p>

        <div className="flex gap-3 pt-2">
          <button
            onClick={assign}
            disabled={busy || !planId}
            className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
          >
            Assign plan
          </button>
          <button
            onClick={saveOverride}
            disabled={busy}
            className="text-gray-600 hover:underline"
          >
            Just update status/dates
          </button>
        </div>
      </div>
    </div>
  );
}

function ModulesTab({ shop, onChange }) {
  // What the plan OFFERS. What is on is that, minus what the owner switched off
  const [modules, setModules] = useState(shop.offered_modules || {});
  const ownerOff = shop.owner_disabled_modules || {};
  const [busy, setBusy] = useState(false);

  const toggle = (key) => {
    setModules((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));
  };

  const save = async () => {
    setBusy(true);
    try {
      await platformAPI.updateModules(shop.id, modules);
      onChange();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-lg bg-white rounded-xl shadow p-5 space-y-4">
      <p className="text-sm text-gray-500">
        What this shop is <strong>offered</strong>. The owner can switch things off within it, never on beyond it.
      </p>
      {MODULES.map((mod) => {
        const enabled = modules[mod.key] !== false;
        return (
          <div key={mod.key} className="flex items-center justify-between border-b border-gray-100 pb-3 last:border-0">
            <div>
              <p className="font-medium text-gray-800">{mod.label}</p>
              <p className="text-xs text-gray-400">
                {mod.enforced ? "Enforced by the API" : "Stored only — not yet enforced"}
              </p>
              {ownerOff[mod.key] && (
                <p className="text-xs text-amber-700">The owner has switched this off</p>
              )}
            </div>
            <button
              onClick={() => toggle(mod.key)}
              className={`w-12 h-6 rounded-full transition-colors relative ${
                enabled ? "bg-green-500" : "bg-gray-300"
              }`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                  enabled ? "translate-x-6" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>
        );
      })}
      <button
        onClick={save}
        disabled={busy}
        className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
      >
        {busy ? "Saving…" : "Save modules"}
      </button>
    </div>
  );
}

function AuditLogTab({ shopId }) {
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    platformAPI.getAuditLog(shopId).then(({ data }) => setEntries(data));
  }, [shopId]);

  return (
    <div className="bg-white rounded-xl shadow divide-y divide-gray-100">
      {entries.map((entry) => (
        <div key={entry.id} className="px-4 py-3 text-sm">
          <div className="flex justify-between">
            <span className="font-medium text-gray-800">{entry.action}</span>
            <span className="text-gray-400">{new Date(entry.created_at).toLocaleString()}</span>
          </div>
          <div className="text-gray-500">{entry.actor_user_id ? entry.actor_name : "System (automatic)"}</div>
          {entry.details && Object.keys(entry.details).length > 0 && (
            <pre className="text-xs text-gray-400 mt-1 whitespace-pre-wrap">
              {JSON.stringify(entry.details, null, 2)}
            </pre>
          )}
        </div>
      ))}
      {entries.length === 0 && (
        <p className="px-4 py-8 text-center text-gray-400">Nothing yet.</p>
      )}
    </div>
  );
}

/** Which rollout group this shop is in: what decides whether it gets a new feature early. */
function RingPicker({ shop, onChange }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const choose = async (ring) => {
    if (ring === shop.ring) return;
    setBusy(true);
    setError("");
    try {
      await platformAPI.setRing(shop.id, ring);
      onChange();
    } catch (err) {
      setError(err.response?.data?.error || "Could not change the ring");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-6 flex flex-wrap items-center gap-2 text-sm">
      <span className="text-gray-500">Release ring:</span>
      {RINGS.map((ring) => (
        <button
          key={ring.key}
          type="button"
          disabled={busy}
          title={ring.hint}
          onClick={() => choose(ring.key)}
          className={`px-3 py-1 rounded-full font-medium border ${
            shop.ring === ring.key ? `${RING_STYLE[ring.key]} border-transparent` : "bg-white text-gray-500 border-gray-200"
          }`}
        >
          {ring.label}
        </button>
      ))}
      {error && <span className="text-red-600">{error}</span>}
    </div>
  );
}

export default function ShopDetail() {
  const { id } = useParams();
  const [shop, setShop] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await platformAPI.getShop(id);
      setShop(data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load shop");
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <p className="text-red-600">{error}</p>;
  if (!shop) return <p className="text-gray-500">Loading…</p>;

  return (
    <div>
      <Link to="/shops" className="text-sm text-gray-500 hover:underline">← Shops</Link>
      <h1 className="text-2xl font-bold text-gray-900 mt-1 mb-1">{shop.name}</h1>
      <p className="text-sm text-gray-500 mb-2">
        /{shop.slug}
        {shop.owner_name && <span> · {shop.owner_name} ({shop.owner_email})</span>}
      </p>
      {shop.deletion_scheduled_at && !shop.deleted_at && (
        <p className="mb-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
          Scheduled for deletion — purged after {new Date(shop.deletion_purge_after).toLocaleString()}. Restore it from
          the Delete tab.
        </p>
      )}
      <RingPicker shop={shop} onChange={load} />

      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview" && <OverviewTab shopId={shop.id} />}
      {tab === "Subscription" && (
        <>
          <PackCard shop={shop} onChange={load} />
          {/* A pack member's subscription is its paying shop's, changed there */}
          {!shop.billing_shop_id && <SubscriptionTab shop={shop} onChange={load} />}
        </>
      )}
      {tab === "Modules" && <ModulesTab shop={shop} onChange={load} />}
      {tab === "Devices" && <ShopDevicesTab shop={shop} onChange={load} />}
      {tab === "Data" && <ShopDataTab shop={shop} />}
      {tab === "Delete" && <ShopDeletionTab shop={shop} onChange={load} />}
      {tab === "Audit log" && <AuditLogTab shopId={shop.id} />}
    </div>
  );
}
